import { getCurrentInstance, onUnmounted, ref, type Ref } from "vue"

export default function useDelayedFunction(fn: (args?: any) => any, delayMs: number = 350) {
  const timeout: Ref<ReturnType<typeof setTimeout> | null> = ref(null)
  const isScheduled = ref(false)

  let enabled = true

  async function schedule(args?: any) {
    cancel()
    isScheduled.value = true
    return new Promise((resolve) => {
      timeout.value = setTimeout(async () => {
        cancel()
        resolve(await callFunctionIfEnabled(args))
      }, delayMs)
    })
  }

  function disable() {
    cancel()
    enabled = false
  }

  function enable() {
    enabled = true
  }

  async function callFunctionIfEnabled(args?: any) {
    if (enabled) {
      return await fn(args)
    }
  }

  function executeNowIfScheduled() {
    if (timeout.value) {
      cancel()
      return callFunctionIfEnabled()
    }
  }

  /* Cancels the current timeout. Returns true if there was a timeout to be cancelled, false is there was not timeout. */
  function cancel() {
    isScheduled.value = false
    if (timeout.value) {
      clearTimeout(timeout.value)
      timeout.value = null
      return true
    }
    return false
  }

  // Only register component lifecycle hooks if we're in a component context
  // This allows the composable to be used outside of setup() as well
  if (getCurrentInstance()) {
    onUnmounted(async () => {
      if (cancel()) {
        await callFunctionIfEnabled()
      }
    })
  }

  return {
    schedule,
    disable,
    enable,
    cancel,
    executeNowIfScheduled,
    callFunctionIfEnabled,
    isScheduled
  }
}
